import { numeroEscrito } from "@/lib/numeroEscrito";
import type { RolDeInforme, ValoresLegalesDelRol } from "@/lib/informes";

/* ---------------------------------------------------------------------------
   Los DATOS LEGALES de cada rol (la tabla con que abre el capítulo legal de un
   informe): predio, tres superficies, destino y avalúo del SII e inscripción de
   dominio. Se editan en "Descripción de la propiedad" junto a los deslindes.

   ══ SE EDITAN COMO TEXTO Y SE CONVIERTEN AL GUARDAR ═══════════════════════════

   Una persona escribe «39,30» y «93.822.551», no 39.3 ni 93822551. El estado
   de la pantalla guarda lo TECLEADO (strings) y `legalesAPayload` lo traduce con
   `numeroEscrito`, la misma lectura chilena del modal de comparables. Lo que no
   se puede leer NO se descarta en silencio: vuelve como error del campo y el
   guardado no sale, o la persona creería que quedó algo que no quedó.

   ══ UNA PROPUESTA DE LA IA SOLO LLENA CAMPOS VACÍOS ═══════════════════════════

   Mismo criterio que los deslindes ({@see sumarLaPropuestaDeDescripcion}): lo
   que ya está escrito es de la persona, y por eso «Descartar» puede revertir
   los campos que siguen IGUALES a lo propuesto sin llevarse nada ajeno. La
   superficie de PLANIMETRÍA no se propone nunca: se mide sobre un plano.
--------------------------------------------------------------------------- */

export type CampoLegal = keyof ValoresLegalesDelRol;

/** Todos los campos, en el orden en que se dibujan. */
export const CAMPOS_LEGALES: CampoLegal[] = [
  "nombre_predio",
  "superficie_titulos",
  "superficie_sii",
  "superficie_planimetria",
  "destino_sii",
  "avaluo_fiscal_clp",
  "inscripcion_fojas",
  "inscripcion_numero",
  "inscripcion_anio",
  "inscripcion_cbr",
];

/** Los que la IA puede proponer: todos menos la planimetría. */
export const CAMPOS_LEGALES_PROPONIBLES = CAMPOS_LEGALES.filter(
  (campo) => campo !== "superficie_planimetria",
);

const CAMPOS_DE_SUPERFICIE: CampoLegal[] = ["superficie_titulos", "superficie_sii", "superficie_planimetria"];

/** Lo tecleado en cada campo de UN rol. Vacío es `""`, nunca `null`. */
export type DatosLegalesDelRol = Record<CampoLegal, string>;

export type LegalesPorRol = Record<number, DatosLegalesDelRol>;

export function legalesVacios(): DatosLegalesDelRol {
  return Object.fromEntries(CAMPOS_LEGALES.map((campo) => [campo, ""])) as DatosLegalesDelRol;
}

/** `93822551` → `"93.822.551"`. */
function conMiles(entero: number): string {
  return String(entero).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

/** `"39.30"` (decimal del servidor) → `"39,30"`; un número propuesto `39.3` → `"39,3"`. */
function conComa(valor: string | number): string {
  return String(valor).replace(".", ",");
}

/** Lo que el formulario muestra de un rol guardado. `null` → `""` para que el input siga controlado. */
export function legalesDelRol(rol: RolDeInforme): DatosLegalesDelRol {
  return {
    nombre_predio: rol.nombre_predio ?? "",
    superficie_titulos: rol.superficie_titulos == null ? "" : conComa(rol.superficie_titulos),
    superficie_sii: rol.superficie_sii == null ? "" : conComa(rol.superficie_sii),
    superficie_planimetria: rol.superficie_planimetria == null ? "" : conComa(rol.superficie_planimetria),
    destino_sii: rol.destino_sii ?? "",
    // Cero es un dato (se muestra "0"); solo `null` es vacío.
    avaluo_fiscal_clp: rol.avaluo_fiscal_clp == null ? "" : conMiles(rol.avaluo_fiscal_clp),
    inscripcion_fojas: rol.inscripcion_fojas ?? "",
    inscripcion_numero: rol.inscripcion_numero ?? "",
    inscripcion_anio: rol.inscripcion_anio == null ? "" : String(rol.inscripcion_anio),
    inscripcion_cbr: rol.inscripcion_cbr ?? "",
  };
}

export interface LegalesParaGuardar {
  valores: ValoresLegalesDelRol;
  /** Un mensaje por campo ilegible. Vacío si se puede guardar. */
  errores: Partial<Record<CampoLegal, string>>;
}

/**
 * Traduce lo tecleado a lo que el servidor guarda. Los topes son los de su
 * request: un valor que el servidor rechazaría se avisa acá, en el campo.
 */
export function legalesAPayload(datos: DatosLegalesDelRol): LegalesParaGuardar {
  const errores: Partial<Record<CampoLegal, string>> = {};
  const texto = (campo: CampoLegal): string | null => datos[campo].trim() || null;

  const superficie = (campo: CampoLegal): number | null => {
    const escrito = datos[campo].trim();
    if (escrito === "") return null;

    const valor = numeroEscrito(escrito);
    if (valor === null) {
      errores[campo] = "Escribe un número, por ejemplo 39,30.";
      return null;
    }
    if (valor < 0) {
      errores[campo] = "La superficie no puede ser negativa.";
      return null;
    }
    if (Math.round(valor * 100) / 100 !== valor) {
      errores[campo] = "Usa hasta dos decimales.";
      return null;
    }

    return valor;
  };

  const avaluo = (): number | null => {
    const escrito = datos.avaluo_fiscal_clp.replace(/[$\s]/g, "");
    if (escrito === "") return null;

    const valor = numeroEscrito(escrito);
    if (valor === null) {
      errores.avaluo_fiscal_clp = "Escribe el avalúo en pesos, por ejemplo 93.822.551.";
      return null;
    }
    if (valor < 0) {
      errores.avaluo_fiscal_clp = "El avalúo no puede ser negativo.";
      return null;
    }
    if (!Number.isInteger(valor)) {
      errores.avaluo_fiscal_clp = "El avalúo va en pesos enteros, sin decimales.";
      return null;
    }

    return valor;
  };

  const anio = (): number | null => {
    const escrito = datos.inscripcion_anio.trim();
    if (escrito === "") return null;

    const maximo = new Date().getFullYear() + 1;
    const valor = /^\d{4}$/.test(escrito) ? Number(escrito) : NaN;

    if (!(valor >= 1800 && valor <= maximo)) {
      errores.inscripcion_anio = `Escribe un año de cuatro cifras entre 1800 y ${maximo}.`;
      return null;
    }

    return valor;
  };

  const valores: ValoresLegalesDelRol = {
    nombre_predio: texto("nombre_predio"),
    superficie_titulos: superficie("superficie_titulos"),
    superficie_sii: superficie("superficie_sii"),
    superficie_planimetria: superficie("superficie_planimetria"),
    destino_sii: texto("destino_sii"),
    avaluo_fiscal_clp: avaluo(),
    inscripcion_fojas: texto("inscripcion_fojas"),
    inscripcion_numero: texto("inscripcion_numero"),
    inscripcion_anio: anio(),
    inscripcion_cbr: texto("inscripcion_cbr"),
  };

  return { valores, errores };
}

/** Un campo que la IA escribió en un rol, para poder revertirlo si sigue igual. */
export interface EscritoLegal {
  id: number;
  campo: CampoLegal;
  valor: string;
}

export interface MezclaLegal {
  legales: LegalesPorRol;
  escritos: EscritoLegal[];
}

/** Lo que la IA propone de UN rol; cada clave puede faltar o ser `null`. */
export type PropuestaLegalDeUnRol = { id: number } & Partial<
  Record<CampoLegal, string | number | null | undefined>
>;

/**
 * Escribe lo propuesto SOLO en campos vacíos, sin pisar nada. Ignora un rol
 * que ya no está, los `null`, los textos en blanco y la planimetría (aunque
 * llegue). Un número se muestra como lo escribiría una persona.
 */
export function sumarLaPropuestaLegal(
  actual: LegalesPorRol,
  propuesta: PropuestaLegalDeUnRol[],
  rolesIds: number[],
): MezclaLegal {
  const idsValidos = new Set(rolesIds);
  const escritos: EscritoLegal[] = [];
  let legales = actual;

  for (const fila of propuesta) {
    if (!idsValidos.has(fila.id)) continue;

    for (const campo of CAMPOS_LEGALES_PROPONIBLES) {
      const valor = comoTexto(campo, fila[campo]);
      if (valor === null) continue;

      const delRol = legales[fila.id] ?? legalesVacios();

      // NUNCA PISA: lo escrito es de la persona.
      if (delRol[campo].trim() !== "") continue;

      legales = { ...legales, [fila.id]: { ...delRol, [campo]: valor } };
      escritos.push({ id: fila.id, campo, valor });
    }
  }

  return { legales, escritos };
}

function comoTexto(campo: CampoLegal, valor: string | number | null | undefined): string | null {
  if (valor === null || valor === undefined) return null;

  if (typeof valor === "number") {
    if (!Number.isFinite(valor)) return null;
    if (campo === "avaluo_fiscal_clp") return conMiles(valor);
    return CAMPOS_DE_SUPERFICIE.includes(campo) ? conComa(valor) : String(valor);
  }

  const limpio = valor.trim();

  return limpio === "" ? null : limpio;
}

/** Revierte SOLO los campos que siguen siendo EXACTAMENTE lo propuesto. */
export function descartarLaPropuestaLegal(
  actual: LegalesPorRol,
  escritos: EscritoLegal[],
): LegalesPorRol {
  let legales = actual;

  for (const escrito of escritos) {
    const delRol = legales[escrito.id];

    if (!delRol || delRol[escrito.campo] !== escrito.valor) continue;

    legales = { ...legales, [escrito.id]: { ...delRol, [escrito.campo]: "" } };
  }

  return legales;
}

/** ¿Queda algo de lo que la IA escribió? Para resolver la corrida al guardar. */
export function quedaAlgoDeLoLegalPropuesto(actual: LegalesPorRol, escritos: EscritoLegal[]): boolean {
  return escritos.some((escrito) => (actual[escrito.id]?.[escrito.campo] ?? "").trim() !== "");
}

function sonIguales(uno: DatosLegalesDelRol | undefined, otro: DatosLegalesDelRol | undefined): boolean {
  const a = uno ?? legalesVacios();
  const b = otro ?? legalesVacios();

  return CAMPOS_LEGALES.every((campo) => a[campo] === b[campo]);
}

/** Iguales rol por rol; un rol ausente cuenta como vacío. */
export function mismosLegales(a: LegalesPorRol, b: LegalesPorRol): boolean {
  const ids = new Set([...Object.keys(a), ...Object.keys(b)].map(Number));

  return [...ids].every((id) => sonIguales(a[id], b[id]));
}

/** Cuántos roles cambiaron de datos legales. */
export function contarLegalesCambiados(a: LegalesPorRol, b: LegalesPorRol): number {
  const ids = new Set([...Object.keys(a), ...Object.keys(b)].map(Number));

  return [...ids].filter((id) => !sonIguales(a[id], b[id])).length;
}
